/**
 * Pith Cart Integration — native stack cart API (DEC-127)
 *
 * A "stack" = 1-3 real supplement lines + the app line, each carrying the
 * `Pith Monthly Native` selling plan discovered dynamically per product
 * (native-stack-migration-spec.md §3). Pricing/validation live server-side
 * in the checkout Functions app; theme guards are cosmetic UX only.
 * The Appstle-era legacy path was removed at the #148 cutover (2026-07-26).
 */

/* ========================================
   Native Stack Configuration (#148)
   ======================================== */

const NATIVE_PLAN_GROUP_NAME = 'Pith Monthly Native';

const PITH_STACK = {
  app: {
    variantId: 43809123794987,
    sku: 'pith-app-premium',
    name: 'Pith: Brain Training',
    handle: 'premium-app',
  },
  supplements: [
    {
      variantId: 43806901010475,
      sku: 'RLC4CREA',
      legacySku: 'pith-creatine',
      name: 'Creatine Monohydrate',
      handle: 'creatine',
    },
    {
      variantId: 43806718558251,
      sku: 'AAV9ENER',
      legacySku: 'pith-energy-strips',
      name: 'Energy Strips',
      handle: 'energy-strips',
    },
    {
      variantId: 43798917316651,
      sku: 'JTP4APLU',
      legacySku: 'pith-ashwagandha',
      name: 'Ashwagandha Plus',
      handle: 'ashwagandha-plus',
    },
    {
      variantId: 43645185490987,
      sku: 'VOX4MGNE',
      legacySku: 'pith-magnesium',
      name: 'Magnesium Glycinate',
      handle: 'magnesium-glycinate',
    },
  ],
};

const STACK_TIER_BY_COUNT = { 1: 'starter', 2: 'core', 3: 'complete' };
const STACK_TIER_LABELS = { starter: 'Starter', core: 'Core', complete: 'Complete' };

/* ========================================
   Default stacks per tier
   ======================================== */
// Default composition when a subscribe CTA is used without the customize
// flow. Matches tier-widget.liquid card text: Starter = Foundation;
// Core = Foundation + Recovery; Complete = + Steady Calm.

const PITH_BUNDLES = {
  starter: {
    defaultProducts: [{ variantId: 43806901010475, name: 'Creatine', sku: 'pith-creatine' }],
  },
  core: {
    defaultProducts: [
      { variantId: 43806901010475, name: 'Creatine', sku: 'pith-creatine' },
      { variantId: 43645185490987, name: 'Magnesium Glycinate', sku: 'pith-magnesium' },
    ],
  },
  complete: {
    defaultProducts: [
      { variantId: 43806901010475, name: 'Creatine', sku: 'pith-creatine' },
      { variantId: 43645185490987, name: 'Magnesium Glycinate', sku: 'pith-magnesium' },
      { variantId: 43798917316651, name: 'Ashwagandha Plus', sku: 'pith-ashwagandha' },
    ],
  },
};

/* ========================================
   Supplement Products (for product picker)
   ======================================== */

const SUPPLEMENT_PRODUCTS = [
  {
    variantId: 43806718558251,
    name: 'Energy Strips',
    sku: 'pith-energy-strips',
    subtitle: 'Focused Flow',
  },
  {
    variantId: 43806901010475,
    name: 'Creatine',
    sku: 'pith-creatine',
    subtitle: 'Cognitive Foundation',
  },
  {
    variantId: 43798917316651,
    name: 'Ashwagandha Plus',
    sku: 'pith-ashwagandha',
    subtitle: 'Steady Calm',
  },
  {
    variantId: 43645185490987,
    name: 'Magnesium Glycinate',
    sku: 'pith-magnesium',
    subtitle: 'Rest & Restore',
  },
];

const TIER_CONSTRAINTS = {
  starter: { min: 1, max: 1 },
  core: { min: 2, max: 2 },
  complete: { min: 3, max: 3 },
};

/* ========================================
   ph_did → cart attribute bridge (DEC-109)
   ======================================== */

/* Carries the storefront visitor's PostHog distinct id onto the cart, so the
   order webhook can merge the anonymous ad-clicker (who holds the landing-page
   view and checkout_opened) into the buyer person (who holds the revenue).
   Without it those are two disconnected people and there is no funnel.

   Cart attributes ride to the order as `note_attributes`; the underscore
   prefix matches the internal-attribute convention (_pith_stack). Server side:
   shopify-webhook `extractPhDid` + `shouldAliasPhDid`.

   Design notes:
   - Written at ADD time, not at checkout click. A write racing the navigation
     to /checkout could land after Shopify has already read the cart.
   - Silent no-op when PostHog is blocked or absent — there is simply no id to
     bridge. Campaign attribution does NOT depend on this (it comes from
     Shopify's own landing_site server-side), matching the existing rule that
     paid attribution must never depend on PostHog loading.
   - Never throws and never blocks the cart. */

const PH_DID_ATTRIBUTE = '_ph_did';
let phDidSyncInFlight = false;

function currentPhDid() {
  try {
    return (
      (window.analytics &&
        typeof window.analytics.getDistinctId === 'function' &&
        window.analytics.getDistinctId()) ||
      null
    );
  } catch (err) {
    return null;
  }
}

/* `cart` is optional; when supplied (callers usually just fetched it) its
   attributes are used to skip a redundant write. */
async function syncPhDidToCart(cart) {
  try {
    const did = currentPhDid();
    if (!did || phDidSyncInFlight) return;
    if (cart && cart.attributes && cart.attributes[PH_DID_ATTRIBUTE] === did) return;

    phDidSyncInFlight = true;
    const attributes = {};
    attributes[PH_DID_ATTRIBUTE] = did;
    /* /cart/update.js merges attributes — it does not clear ones we omit. */
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    });
  } catch (err) {
    /* An analytics bridge must never break a cart. */
  } finally {
    phDidSyncInFlight = false;
  }
}

window.pithPhDid = { sync: syncPhDidToCart, current: currentPhDid, attribute: PH_DID_ATTRIBUTE };

/* ========================================
   Add Bundle to Cart
   ======================================== */

/* Analytics: value (dollars) of the item(s) an add returned (tracking-plan web spoke). */
function pithAddedValue(added) {
  if (!added) return 0;
  const items = added.items || [added];
  const cents = items.reduce(function (s, it) {
    return s + (it.final_line_price || it.line_price || it.price || 0);
  }, 0);
  return Math.round(cents) / 100;
}
function pithTrackAdd(props) {
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track('add_to_cart', props);
  }
}

/* ========================================
   Native Stack Add Path (#148)
   ======================================== */

/* Per-page-load cache: product handle → native selling plan id. */
const nativePlanCache = {};

/**
 * Discover the native monthly selling plan for a product, by GROUP NAME —
 * never by hardcoded plan id (spec §1.2). Fails loud when absent.
 */
async function getNativeSellingPlanId(handle) {
  if (nativePlanCache[handle]) return nativePlanCache[handle];

  const res = await fetch('/products/' + handle + '.js');
  if (!res.ok) {
    throw new Error('Could not load product data for "' + handle + '"');
  }
  const product = await res.json();
  const groups = product.selling_plan_groups || [];
  const group = groups.find(function (g) {
    return g.name === NATIVE_PLAN_GROUP_NAME;
  });
  const plan = group && group.selling_plans && group.selling_plans[0];
  if (!plan) {
    throw new Error(
      'Selling plan group "' +
        NATIVE_PLAN_GROUP_NAME +
        '" not found on "' +
        handle +
        '" — has it been created and attached in the Shopify Subscriptions app?',
    );
  }
  nativePlanCache[handle] = plan.id;
  return plan.id;
}

/* Map a caller-supplied product ({variantId|sku|handle}) to the canonical
   stack entry. Callers pass legacy-shaped objects (variantId + legacy sku);
   variant id is the primary key. */
function resolveStackEntry(product) {
  if (!product) return null;
  return (
    PITH_STACK.supplements.find(function (s) {
      return (
        (product.variantId && s.variantId === product.variantId) ||
        (product.handle && s.handle === product.handle) ||
        (product.sku && (s.sku === product.sku || s.legacySku === product.sku))
      );
    }) || null
  );
}

/**
 * Cosmetic UX guard (spec §3.1): one stack per cart. If the cart already
 * holds plan-carrying stack lines, ask replace-vs-keep before adding.
 * The checkout validation function is the real enforcement (V1/V3).
 * Returns false when the shopper chose to keep the existing stack.
 */
async function guardExistingStack() {
  const cartRes = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
  const cart = await cartRes.json();
  const planLines = cart.items.filter(function (item) {
    return !!item.selling_plan_allocation;
  });
  const hasApp = planLines.some(function (item) {
    return item.variant_id === PITH_STACK.app.variantId;
  });
  if (!hasApp) return true;

  const replace = window.confirm('Your cart already has a Pith stack. Replace it with this one?');
  if (!replace) return false;

  // Remove every plan-carrying line in ONE atomic /cart/update.js call.
  // (Sequential /cart/change.js calls hit Shopify's cart rate limit mid-loop,
  // leaving half-removed stacks — live finding 2026-07-19.)
  const updates = {};
  planLines.forEach(function (item) {
    updates[item.key] = 0;
  });
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates: updates }),
  });
  if (!res.ok) {
    throw new Error('Could not remove the existing stack — please try again.');
  }
  return true;
}

/**
 * Add a native stack: 1-3 real supplement lines + the app line, every line
 * quantity 1 and carrying the native monthly plan. No pricing properties —
 * the discount function prices the composition server-side. `_pith_stack`
 * rides along for analytics continuity only.
 */
async function addStackToCart(tier, customProducts) {
  const source =
    customProducts && customProducts.length
      ? customProducts
      : (PITH_BUNDLES[tier] || {}).defaultProducts || [];

  // Resolve to canonical entries, dedupe, cap at 3 (mirrors V4).
  const entries = [];
  source.forEach(function (p) {
    const entry = resolveStackEntry(p);
    if (entry && entries.indexOf(entry) === -1 && entries.length < 3) {
      entries.push(entry);
    }
  });
  if (!entries.length) {
    throw new Error('No valid supplements selected for the stack');
  }

  const tierName = STACK_TIER_BY_COUNT[entries.length] || tier;

  // Build the FULL line set (incl. all selling-plan lookups) BEFORE touching
  // the cart — if any lookup fails we bail with the cart untouched, and the
  // removal→add window stays as small as possible (live finding 2026-07-19).
  const lines = [];
  for (const entry of entries) {
    lines.push({
      id: entry.variantId,
      quantity: 1,
      selling_plan: await getNativeSellingPlanId(entry.handle),
      properties: { _pith_stack: tierName },
    });
  }
  lines.push({
    id: PITH_STACK.app.variantId,
    quantity: 1,
    selling_plan: await getNativeSellingPlanId(PITH_STACK.app.handle),
    properties: { _pith_stack: tierName },
  });

  const proceed = await guardExistingStack();
  if (!proceed) return null;

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: lines }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    // Resync the drawer/badge — the old stack may already be gone, and stale
    // UI here is exactly how the 7/19 checkout-mismatch confusion happened.
    await refreshCartDrawer().catch(function () {});
    throw new Error(error.description || error.message || 'Failed to add to cart');
  }

  const addedItems = await res.json();

  pithTrackAdd({ tier: tierName, value: pithAddedValue(addedItems), type: 'bundle' });

  await refreshCartDrawer();

  return addedItems;
}

/* Standalone app subscription on the native plan (spec §3.1). */
async function addNativeAppSubscription() {
  const planId = await getNativeSellingPlanId(PITH_STACK.app.handle);

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: PITH_STACK.app.variantId, quantity: 1, selling_plan: planId }],
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.description || error.message || 'Failed to add subscription to cart');
  }

  const addedItem = await res.json();

  pithTrackAdd({ value: pithAddedValue(addedItem), type: 'app_subscription' });

  await refreshCartDrawer();
  return addedItem;
}

/* Back-compat alias: every subscribe caller (tier-widget/comparison,
   bundle-modal, quiz, upgrade, product-hero) routes through this name. */
async function addBundleToCart(tier, customProducts) {
  return addStackToCart(tier, customProducts);
}

/* ========================================
   Add One-Off Item to Cart
   ======================================== */

async function addOneOffToCart(variantId) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.description || error.message || 'Failed to add to cart');
  }

  const addedItem = await res.json();

  pithTrackAdd({ value: pithAddedValue(addedItem), type: 'one_off' });

  // Refresh cart drawer without page reload
  await refreshCartDrawer();

  return addedItem;
}

/* ========================================
   Add Standalone Subscription to Cart
   ======================================== */
// Premium App sold on its own as a recurring subscription (DEC-080), no
// bundle/supplement attached. Params are back-compat: callers still pass
// (variantId, sellingPlanId) but the native plan is discovered by group name.
async function addStandaloneSubscription() {
  return addNativeAppSubscription();
}

/* ========================================
   Refresh Cart Drawer (AJAX, no reload)
   ======================================== */

async function refreshCartDrawer() {
  try {
    // Fetch full cart data
    const cartRes = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    const cart = await cartRes.json();

    // Cache totals for analytics (checkout_opened reads these)
    window.__pithCart = { item_count: cart.item_count, total_price: cart.total_price };

    // Stamp the PostHog distinct id onto the cart (DEC-109). Deliberately not
    // awaited: drawer rendering must not wait on an analytics write.
    syncPhDidToCart(cart);

    // Update cart count in header
    if (window.pithCart) {
      window.pithCart.updateCount(cart.item_count);
    }

    // Rebuild cart drawer body with fade transition
    const drawerBody = document.getElementById('cart-drawer-body');
    if (!drawerBody) return;

    drawerBody.style.opacity = '0';
    drawerBody.style.transition = 'opacity 0.15s ease';

    // Small delay for fade-out before swapping content
    await new Promise(function (resolve) {
      setTimeout(resolve, 150);
    });

    if (cart.item_count === 0) {
      drawerBody.innerHTML =
        '<div class="cart-drawer__empty">' +
        '<p class="text-body-lg">Your cart is empty.</p>' +
        '<a href="/pages/bundles" class="btn btn--primary cart-drawer__empty-cta" data-action="close-cart" style="margin-top: var(--space-lg);">Get Started</a>' +
        '</div>';
    } else {
      drawerBody.innerHTML = buildCartItemsHtml(cart);
    }

    // Fade content back in
    requestAnimationFrame(function () {
      drawerBody.style.opacity = '1';
    });

    // Open the drawer
    if (window.pithCart) {
      window.pithCart.open();
    }
  } catch (err) {
    console.error('Cart drawer refresh failed:', err);
    // Fallback: reload
    window.location.href = window.location.pathname + '?cart_open=true';
  }
}

/* ========================================
   Cart Drawer HTML
   ======================================== */

/* One standalone cart line (image, title, sub-label, price, remove). */
function renderLineItemHtml(item, line) {
  var isSubscription = !!item.selling_plan_allocation;
  var html = '<div class="cart-drawer__item" data-line="' + escapeHtml(line) + '">';
  if (item.image) {
    html +=
      '<img class="cart-drawer__item-image" src="' +
      escapeHtml(item.image) +
      '" alt="' +
      escapeHtml(item.product_title) +
      '" width="52" height="52" loading="lazy">';
  }
  html += '<div class="cart-drawer__item-info">';
  html += '<p class="cart-drawer__item-title">' + escapeHtml(item.product_title) + '</p>';
  html +=
    '<p class="cart-drawer__item-sub">' +
    (isSubscription ? 'Monthly subscription — renews automatically' : 'One-time purchase') +
    '</p>';
  html += '<div class="cart-drawer__item-bottom">';
  html += '<p class="cart-drawer__item-price">' + escapeHtml(formatMoney(item.final_line_price));
  if (isSubscription) html += '<span class="cart-drawer__item-per">/mo</span>';
  html += '</p>';
  if (!isSubscription) {
    html +=
      '<div class="cart-drawer__quantity">' +
      '<button class="cart-drawer__qty-btn" data-action="decrease" data-line="' +
      escapeHtml(line) +
      '" aria-label="Decrease quantity">−</button>' +
      '<span class="cart-drawer__qty-value">' +
      escapeHtml(item.quantity) +
      '</span>' +
      '<button class="cart-drawer__qty-btn" data-action="increase" data-line="' +
      escapeHtml(line) +
      '" aria-label="Increase quantity">+</button>' +
      '</div>';
  }
  html +=
    '<button class="cart-drawer__remove" type="button" data-action="remove" data-line="' +
    escapeHtml(line) +
    '">Remove</button>';
  html += '</div></div></div>';
  return html;
}

/**
 * Cart shape (spec §3.2): plan-carrying lines on KNOWN Pith products group
 * into one stack when the app line plus at least one supplement is present.
 * Everything else renders as a normal line — an app-only subscription keeps
 * its standard $19 presentation.
 */
function classifyCartForStacks(cart) {
  var stackCandidates = [];
  var rest = [];

  cart.items.forEach(function (item, index) {
    var entry = { item: item, line: index + 1 };
    var isPlan = !!item.selling_plan_allocation;
    var isKnown =
      item.variant_id === PITH_STACK.app.variantId ||
      !!resolveStackEntry({ variantId: item.variant_id });
    if (isPlan && isKnown) {
      stackCandidates.push(entry);
    } else {
      rest.push(entry);
    }
  });

  var appLine = null;
  var suppLines = [];
  stackCandidates.forEach(function (entry) {
    if (entry.item.variant_id === PITH_STACK.app.variantId && !appLine) {
      appLine = entry;
    } else {
      suppLines.push(entry);
    }
  });

  if (appLine && suppLines.length > 0) {
    return { stack: { appLine: appLine, suppLines: suppLines }, rest: rest };
  }
  return { stack: null, rest: null };
}

/* The grouped stack card: header with tier + recurring total, component rows
   (struck list price where discounted), app row struck to $0.00. */
/* Cart-level automatic discounts (e.g. the DEC-131 intro price): summed amount + first title. */
function cartIntro(cart) {
  var apps = (cart && cart.cart_level_discount_applications) || [];
  var amount = 0;
  var title = '';
  apps.forEach(function (d) {
    amount += d.total_allocated_amount || 0;
    if (!title) title = d.title || '';
  });
  return { amount: amount, title: title };
}

function stackThumbHtml(item) {
  if (item.image) {
    return (
      '<img class="cart-stack__thumb" src="' +
      escapeHtml(item.image) +
      '" alt="" width="36" height="36" loading="lazy">'
    );
  }
  return '<span class="cart-stack__thumb" aria-hidden="true"></span>';
}

/* The stack card. Mirrors the Liquid markup in snippets/cart-drawer.liquid — keep in sync. */
function renderStackCardHtml(stack, intro) {
  var tierKey = STACK_TIER_BY_COUNT[stack.suppLines.length];
  var tierLabel = STACK_TIER_LABELS[tierKey] || 'Pith';
  var allLines = stack.suppLines.concat([stack.appLine]);
  var total = 0;
  var keys = [];
  allLines.forEach(function (entry) {
    total += entry.item.final_line_price || 0;
    keys.push(entry.item.key);
  });
  var introOn = intro && intro.amount > 0 && total > 0;
  var introTotal = introOn ? Math.max(0, total - intro.amount) : total;
  var introPct = introOn ? Math.round((intro.amount * 100) / total) : 0;

  var html = '<section class="cart-stack" aria-label="' + escapeHtml(tierLabel) + ' Stack">';
  html += '<div class="cart-stack__head">';
  html += '<p class="cart-stack__eyebrow">' + escapeHtml(tierLabel) + ' Stack &middot; Monthly</p>';
  html +=
    '<button class="cart-stack__remove" type="button" data-action="remove-stack" data-keys="' +
    escapeHtml(keys.join(',')) +
    '">Remove stack</button>';
  html += '</div>';

  html += '<div class="cart-stack__pricing"><p class="cart-stack__price">';
  html +=
    '<span class="cart-stack__amount">' +
    escapeHtml(formatMoney(introTotal)) +
    '</span><span class="cart-stack__per">/mo</span>';
  if (introOn) {
    if (introPct >= 5) html += '<span class="cart-stack__badge">' + introPct + '% off</span>';
    html += '<s class="cart-stack__was">' + escapeHtml(formatMoney(total)) + '/mo</s>';
  }
  html += '</p>';
  if (introOn) {
    html +=
      '<p class="cart-stack__intro">' +
      escapeHtml(intro.title) +
      ', then ' +
      escapeHtml(formatMoney(total)) +
      '/mo.</p>';
  }
  html += '</div>';

  html += '<ul class="cart-stack__lines">';
  stack.suppLines.forEach(function (entry) {
    var item = entry.item;
    html += '<li class="cart-stack__line" data-line="' + escapeHtml(entry.line) + '">';
    html += stackThumbHtml(item);
    html += '<span class="cart-stack__line-title">' + escapeHtml(item.product_title) + '</span>';
    html += '<span class="cart-stack__line-price">';
    if (item.original_line_price > item.final_line_price) {
      html += '<s>' + escapeHtml(formatMoney(item.original_line_price)) + '</s> ';
    }
    html += escapeHtml(formatMoney(item.final_line_price)) + '</span>';
    html +=
      '<button class="cart-stack__line-remove" type="button" data-action="remove" data-line="' +
      escapeHtml(entry.line) +
      '">Remove</button>';
    html += '</li>';
  });
  var appItem = stack.appLine.item;
  html += '<li class="cart-stack__line cart-stack__line--app">';
  html += stackThumbHtml(appItem);
  html += '<span class="cart-stack__line-title">' + escapeHtml(appItem.product_title) + '</span>';
  html += '<span class="cart-stack__line-price">';
  if (appItem.final_line_price === 0) {
    html +=
      '<s>' +
      escapeHtml(formatMoney(appItem.original_line_price)) +
      '</s> <em class="cart-stack__included">Included</em>';
  } else {
    html += escapeHtml(formatMoney(appItem.final_line_price));
  }
  html += '</span><span class="cart-stack__line-spacer" aria-hidden="true"></span></li>';
  html += '</ul>';
  html += '<p class="cart-stack__note">Ships and renews together, monthly.</p>';
  html += '</section>';
  return html;
}

function buildCartItemsHtml(cart) {
  var itemsHtml = '<div class="cart-drawer__items">';
  var hasPlanLines = cart.items.some(function (item) {
    return !!item.selling_plan_allocation;
  });

  var intro = cartIntro(cart);
  var grouped = classifyCartForStacks(cart);
  var stackOnly = false;
  if (grouped.stack) {
    itemsHtml += renderStackCardHtml(grouped.stack, intro);
    stackOnly = grouped.rest.length === 0;
    grouped.rest.forEach(function (entry) {
      itemsHtml += renderLineItemHtml(entry.item, entry.line);
    });
  } else {
    cart.items.forEach(function (item, index) {
      itemsHtml += renderLineItemHtml(item, index + 1);
    });
  }
  itemsHtml += '</div>';

  // Trust signals — subscription-specific lines only render when a
  // plan-carrying line exists (#140).
  var showSubDisclosures = hasPlanLines;
  // 2026-09-13: built here, appended INSIDE the footer below so reassurance sits
  // directly above the Checkout button. Must stay in step with cart-drawer.liquid —
  // tests/cart-drawer.spec.js asserts Liquid/JS render parity.
  var trustHtml = '<div class="cart-drawer__trust">';
  trustHtml +=
    '<p><svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>30-day money-back guarantee</p>';
  if (showSubDisclosures) {
    trustHtml +=
      '<p><svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>Free shipping on all subscriptions</p>';
  }
  trustHtml += '</div>';

  // Summary: pre-discount items, each cart-level discount, then the total.
  var discountApps = (cart && cart.cart_level_discount_applications) || [];
  var summary = '<dl class="cart-summary">';
  if (discountApps.length) {
    var tierLabel = '';
    if (grouped.stack && stackOnly) {
      tierLabel = STACK_TIER_LABELS[STACK_TIER_BY_COUNT[grouped.stack.suppLines.length]] || 'Pith';
    }
    summary +=
      '<div class="cart-summary__row"><dt>' +
      (tierLabel ? escapeHtml(tierLabel) + ' Stack, monthly' : 'Items') +
      '</dt><dd>' +
      escapeHtml(formatMoney(cart.items_subtotal_price)) +
      '</dd></div>';
    discountApps.forEach(function (d) {
      summary +=
        '<div class="cart-summary__row cart-summary__row--discount"><dt>' +
        escapeHtml(d.title || 'Discount') +
        '</dt><dd>&minus;' +
        escapeHtml(formatMoney(d.total_allocated_amount || 0)) +
        '</dd></div>';
    });
  }
  summary +=
    '<div class="cart-summary__row cart-summary__row--total"><dt>Subtotal</dt><dd>' +
    escapeHtml(formatMoney(cart.total_price)) +
    '</dd></div></dl>';

  itemsHtml += '<div class="cart-drawer__footer">' + summary;
  if (showSubDisclosures) {
    itemsHtml +=
      '<p class="cart-drawer__terms">Your subscription renews monthly. You can pause, swap products, or cancel anytime from your account dashboard.</p>';
  }
  itemsHtml +=
    trustHtml +
    '<a href="/checkout" class="btn btn--primary cart-drawer__checkout">Checkout</a>' +
    '<button class="btn btn--ghost cart-drawer__continue" data-action="close-cart">Continue shopping</button>' +
    '</div>';

  return itemsHtml;
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

// Escape dynamic values before interpolating into HTML strings.
// Covers both element text and double/single-quoted attribute contexts.
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/* ========================================
   Expose globally
   ======================================== */

window.pithBundles = {
  addBundle: addBundleToCart,
  addStack: addStackToCart,
  addOneOff: addOneOffToCart,
  addAppSubscription: addStandaloneSubscription,
  _refreshDrawer: refreshCartDrawer,
  config: PITH_BUNDLES,
  stackConfig: PITH_STACK,
  supplements: SUPPLEMENT_PRODUCTS,
  constraints: TIER_CONSTRAINTS,
  ready: true,
};
